import axios from "axios";
import { useNavigate } from "react-router-dom";
import Cookies from 'js-cookie'

import { jwtDecode } from "jwt-decode";
import { getAbsoluteExpiry } from "../utils/Utils";
export const isTokenExpired = (token) => {
  try {
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true; // Invalid token
  }
}

export const portInUseForAPI = 8088
const API_BASE_URL = process.env.REACT_APP_API_DOMAIN + `:${portInUseForAPI}`;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

let isRefreshing = false;

const refreshToken = async () => {
  if (isRefreshing) return Cookies.get('idcs_user_assertion');

  isRefreshing = true;

  try {
    const response = await axiosInstance.post(`/api/auth/refresh-token`, {
      refreshToken: Cookies.get('idcs_refresh_token'),
      role: Cookies.get('role')
    });

    if (response?.status === 200 && response?.data?.statusCode === 200) {
      const absoluteExpTime = getAbsoluteExpiry({
        expiresIn: response.data.data.expires_in
      });

      const newToken = response.data.data.access_token;

      Cookies.set('idcs_user_assertion', newToken);
      Cookies.set('idcs_refresh_token', response.data.data.refresh_token);

      localStorage.setItem('sessionExpTime', absoluteExpTime);

      return newToken;
    }
  } catch (error) {
    console.log(error, "error");
  } finally {
    isRefreshing = false;
  }

  return null;
};

// 📌 Request Interceptor to Handle Authorization & Dynamic Content-Type
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = Cookies.get('idcs_user_assertion')
    //Stop request completely if no token
    if (!token) {
      return Promise.reject(new axios.Cancel("Token missing"));
    }
    const role = Cookies.get('role')
    const auth_id = localStorage.getItem('auth_state')

    const queryParams = config.params;        // <-- URL query params (object)
    const exp = localStorage.getItem('sessionExpTime');
    const now = Date.now();
    // const FIVE_MIN = 5 * 60 * 1000;
    
    // let updatedToken = token;

    // if (token && exp && (exp - now) < FIVE_MIN) {
    //   updatedToken = await refreshToken();
    // }
    const hasRole = role?.toLowerCase(); 
    const isVendor = hasRole === 'vendor';
    const isBuyer = hasRole === 'buyer';

    if (isTokenExpired(token) || (exp && now >= Number(exp))) {
      console.log("Token expired → blocking API call");
      const tokenId = Cookies.get('idcs_id_token');

      Cookies.remove('idcs_user_assertion', { path: '/' });
      Cookies.remove('idcs_refresh_token', { path: '/' });
      Cookies.remove('idcs_id_token', { path: '/' });
      Cookies.remove('role', { path: '/' });
      localStorage.clear();
      sessionStorage.clear();
      const baseUrl = process.env.REACT_APP_ANOTHER_API_URL;
      const logoutUrl =
          process.env.NODE_ENV === "development"
              ? `${baseUrl}/logout?port=${window.location.port}&role=${role}&token-id=${tokenId}&redirect=/session-timeout`
              : `${baseUrl}/logout?role=${role}&token-id=${tokenId}&redirect=/session-timeout`;

      window.location.replace(logoutUrl);
      // window.location.replace("/session-timeout");

      return Promise.reject(new axios.Cancel("Token expired"));
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.role = role;
      config.headers['X-Trace-Id'] = auth_id;
      if (isVendor || isBuyer) {
        const user = JSON.parse(localStorage.getItem('userData'))
        config.headers["X-Source-Ref"] = isBuyer ?  user?.hftOracleId : queryParams?.vendorId ? queryParams?.vendorId : user?.supplierId
      }
    }

    // Ensure Content-Type is set dynamically if not already specified
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json"; // Default type
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 📌 Response Interceptor to Handle Global Errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorMessage = "Something went wrong. Please try again later.";
    if (error.response) {
      const status = error.response.status;

      if (status === 401) {
        const token = Cookies.get('idcs_user_assertion');

        if (isTokenExpired(token)) {
          window.location.href = "/session-timeout";
        } else {
          window.location.href = "/unauthorized-access";
        }
      } 
      else if (status === 403) {
        window.location.href = "/access-forbidden";
      } else if (status === 500) {
        errorMessage = "Something went wrong. Please try again later.";
      } 
      // else if (status === 404) {
      //   window.location.href = "/not-found";
      // } 
    }
    else if (error.request) {
      window.location.href = "/service-unavailable";
    }
    else {
      window.location.href = "/unexpected-error";
    }

    const apiErrorEvent = new CustomEvent("AXIOS_API_ERROR", {
      detail: { message: errorMessage }
    });
    window.dispatchEvent(apiErrorEvent);

    return Promise.reject(error);
  }
);

export default axiosInstance;
